import { useRef, useState } from 'react';
import { SUB_ONBOARDING_MAX_FILE_MB, SUB_ONBOARDING_MAX_FILE_SIZE } from '@hts/shared';

interface FileUploadProps {
  label: string;
  required?: boolean;
  accept?: string;
  file: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  hint?: string;
}

export function FileUpload({ label, required, accept, file, onChange, error, hint }: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  const handleFile = (next: File | null) => {
    if (next && next.size > SUB_ONBOARDING_MAX_FILE_SIZE) {
      setSizeError(
        `That file is ${(next.size / 1024 / 1024).toFixed(1)} MB — the limit is ${SUB_ONBOARDING_MAX_FILE_MB} MB. ` +
          'On a phone, share the photo at Medium or Small size, or take a new photo at lower resolution.',
      );
      onChange(null);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setSizeError(null);
    onChange(next);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0] ?? null);
  };

  const displayError = sizeError ?? error;

  return (
    <div className="form-group">
      <label>
        {label}
        {required && <span className="required">*</span>}
      </label>
      {hint && <p className="field-hint">{hint}</p>}
      <div
        className={`file-upload${file ? ' has-file' : ''}${dragging ? ' dragging' : ''}${displayError ? ' has-error' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        />
        {file ? (
          <div className="file-upload-content">
            <span className="file-upload-icon file-upload-icon-success">&#10003;</span>
            <p>
              <span className="file-name">{file.name}</span>
              <br />
              <span className="file-meta">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
              <br />
              <span className="file-action">Click or drop a new file to replace</span>
            </p>
          </div>
        ) : (
          <div className="file-upload-content">
            <span className="file-upload-icon">&#128206;</span>
            <p>
              <strong>Click here to choose a file</strong>
              <br />
              <span className="file-meta">or drag and drop your file here</span>
              <br />
              <span className="file-meta">Accepted: PDF, JPG, or PNG (max {SUB_ONBOARDING_MAX_FILE_MB} MB)</span>
            </p>
          </div>
        )}
      </div>
      {displayError && <p className="error-text">{displayError}</p>}
    </div>
  );
}
